/**
 * Offer Modal - Full screen modal to display active offers
 * Shows when user logs in and there's an active offer
 */
import { useEffect, useState, useCallback } from 'react';
import { X, Clock } from 'lucide-react';
import { Offer, OfferBackgroundStyle } from '@/types';

// Background style configurations
export const OFFER_BACKGROUNDS: Record<OfferBackgroundStyle, {
  gradient: string;
  accent1: string;
  accent2: string;
  buttonText: string;
  label: string;
}> = {
  purple: {
    gradient: 'from-surface-2',
    accent1: 'bg-accent/100/20',
    accent2: 'bg-accent/10',
    buttonText: 'text-accent',
    label: 'Púrpura',
  },
  blue: {
    gradient: 'from-surface-2',
    accent1: 'bg-info/100/20',
    accent2: 'bg-info/10',
    buttonText: 'text-info',
    label: 'Azul',
  },
  green: {
    gradient: 'from-surface-2',
    accent1: 'bg-success/100/20',
    accent2: 'bg-success/10',
    buttonText: 'text-success',
    label: 'Verde',
  },
  orange: {
    gradient: 'from-surface-2',
    accent1: 'bg-danger/100/20',
    accent2: 'bg-warning/10',
    buttonText: 'text-warning',
    label: 'Naranja',
  },
  gold: {
    gradient: 'from-surface-2',
    accent1: 'bg-warning/100/20',
    accent2: 'bg-accent/10',
    buttonText: 'text-accent',
    label: 'Dorado',
  },
  dark: {
    gradient: 'from-surface-2 to-black',
    accent1: 'bg-surface-20/20',
    accent2: 'bg-surface-3/10',
    buttonText: 'text-ink',
    label: 'Elegante',
  },
  rainbow: {
    gradient: 'from-surface-2',
    accent1: 'bg-info/100/20',
    accent2: 'bg-accent/10',
    buttonText: 'text-accent',
    label: 'Multicolor',
  },
};

// Countdown timer hook
function useCountdown(endDate: any) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    expired: false,
  });

  useEffect(() => {
    const targetDate = endDate?.toDate ? endDate.toDate() : new Date(endDate);

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = targetDate.getTime();
      const difference = target - now;

      if (difference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
        expired: false,
      });
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [endDate]);

  return timeLeft;
}

interface OfferModalProps {
  offer: Offer;
  onClose: () => void;
}

export function OfferModal({ offer, onClose }: OfferModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const countdown = useCountdown(offer.endDate);

  const bgStyle = OFFER_BACKGROUNDS[offer.backgroundStyle || 'purple'];

  useEffect(() => {
    // Animate in
    requestAnimationFrame(() => {
      setIsVisible(true);
    });

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    // Wait for animation to complete
    setTimeout(onClose, 300);
  };

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal Content */}
      <div
        className={`relative w-full max-w-md transform transition-all duration-300 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute -top-12 right-0 p-2 text-ink/80 hover:text-ink transition-colors z-10"
          aria-label="Cerrar"
        >
          <X className="w-8 h-8" />
        </button>

        {/* Card */}
        <div className={`relative overflow-hidden rounded-3xl ${bgStyle.gradient} text-ink shadow-2xl`}>
          {/* Background decorations */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            <div className={`absolute -bottom-24 -left-24 w-64 h-64 ${bgStyle.accent1} rounded-full blur-3xl`} />
            <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 ${bgStyle.accent2} rounded-full blur-3xl`} />
          </div>

          {/* Content */}
          <div className="relative p-8 text-center">
            {/* Image or Icon */}
            <div className="mb-6">
              {offer.imageURL ? (
                <div className="inline-block p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                  <img
                    src={offer.imageURL}
                    alt={offer.title}
                    className="w-28 h-28 object-contain"
                  />
                </div>
              ) : (
                <div className="inline-block p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                  <span className="text-7xl">🎉</span>
                </div>
              )}
            </div>

            {/* Title */}
            <h2 className="text-3xl font-bold mb-3 tracking-tight">
              {offer.title}
            </h2>

            {/* Description */}
            <p className="text-ink/90 text-lg mb-6 leading-relaxed max-w-sm mx-auto">
              {offer.description}
            </p>

            {/* Discount Badge */}
            <div className="inline-flex items-center justify-center mb-6">
              <div className="px-8 py-4 bg-white/20 rounded-full backdrop-blur-sm border border-white/30">
                <span className="text-4xl font-bold tracking-tight">
                  {offer.discountText}
                </span>
              </div>
            </div>

            {/* Countdown Timer */}
            <div className="mb-6">
              <div className="flex items-center justify-center gap-1 text-ink/70 text-xs mb-2">
                <Clock className="w-3 h-3" />
                <span>Termina en</span>
              </div>
              {countdown.expired ? (
                <p className="text-ink/70 text-sm">Oferta finalizada</p>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  {countdown.days > 0 && (
                    <div className="flex flex-col items-center">
                      <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[50px]">
                        <span className="text-2xl font-bold tabular-nums">{countdown.days}</span>
                      </div>
                      <span className="text-[10px] text-ink/60 mt-1">días</span>
                    </div>
                  )}
                  <div className="flex flex-col items-center">
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[50px]">
                      <span className="text-2xl font-bold tabular-nums">{String(countdown.hours).padStart(2, '0')}</span>
                    </div>
                    <span className="text-[10px] text-ink/60 mt-1">horas</span>
                  </div>
                  <span className="text-2xl font-bold text-ink/50 -mt-4">:</span>
                  <div className="flex flex-col items-center">
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[50px]">
                      <span className="text-2xl font-bold tabular-nums">{String(countdown.minutes).padStart(2, '0')}</span>
                    </div>
                    <span className="text-[10px] text-ink/60 mt-1">min</span>
                  </div>
                  <span className="text-2xl font-bold text-ink/50 -mt-4">:</span>
                  <div className="flex flex-col items-center">
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[50px]">
                      <span className="text-2xl font-bold tabular-nums animate-pulse">{String(countdown.seconds).padStart(2, '0')}</span>
                    </div>
                    <span className="text-[10px] text-ink/60 mt-1">seg</span>
                  </div>
                </div>
              )}
            </div>

            {/* CTA Button */}
            <button
              onClick={handleClose}
              className={`w-full py-4 px-8 bg-white ${bgStyle.buttonText} font-bold text-lg rounded-2xl hover:bg-white/90 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg`}
            >
              ¡Aprovecha la oferta!
            </button>
          </div>
        </div>

        {/* Confetti decoration (static) */}
        <div className="absolute -top-4 left-1/4 text-2xl animate-bounce" style={{ animationDelay: '0s' }}>✨</div>
        <div className="absolute -top-2 right-1/4 text-xl animate-bounce" style={{ animationDelay: '0.2s' }}>🎊</div>
        <div className="absolute top-1/4 -left-2 text-lg animate-bounce" style={{ animationDelay: '0.4s' }}>⭐</div>
        <div className="absolute top-1/3 -right-2 text-xl animate-bounce" style={{ animationDelay: '0.6s' }}>🌟</div>
      </div>
    </div>
  );
}

/**
 * Hook to manage offer modal display
 * Shows modal once per session for each offer
 */
export function useOfferModal() {
  const [, setShownOffers] = useState<Set<string>>(() => {
    // Get shown offers from session storage
    const stored = sessionStorage.getItem('shownOffers');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  // Use useCallback to prevent re-triggering useEffect in consumers
  const hasShown = useCallback((offerId: string) => {
    // Always check sessionStorage directly to avoid stale closure issues
    const stored = sessionStorage.getItem('shownOffers');
    const currentSet = stored ? new Set(JSON.parse(stored)) : new Set();
    return currentSet.has(offerId);
  }, []);

  const markAsShown = useCallback((offerId: string) => {
    const stored = sessionStorage.getItem('shownOffers');
    const currentSet = stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
    currentSet.add(offerId);
    sessionStorage.setItem('shownOffers', JSON.stringify([...currentSet]));
    setShownOffers(currentSet);
  }, []);

  return { hasShown, markAsShown };
}
